import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ArrowDownToLine, Menu } from 'lucide-react'
import { CanvasStage, type CanvasHandle, type Phase, type SaveState } from './components/CanvasStage'
import { ProjectRail } from './components/ProjectRail'
import { EmptyState } from './components/EmptyState'
import { Inspector } from './components/Inspector'
import { LayerPanel } from './components/LayerPanel'
const FileMappingDialog = lazy(() => import('./components/FileMappingDialog').then((m) => ({ default: m.FileMappingDialog })))
const RfqDialog = lazy(() => import('./components/RfqDialog').then((m) => ({ default: m.RfqDialog })))
const CommandPalette = lazy(() => import('./components/CommandPalette').then((m) => ({ default: m.CommandPalette })))
const LegalDialog = lazy(() => import('./components/LegalDialog').then((m) => ({ default: m.LegalDialog })))
const RenameDialog = lazy(() => import('./components/ConfirmRename').then((m) => ({ default: m.RenameDialog })))
const ConfirmDialog = lazy(() => import('./components/ConfirmRename').then((m) => ({ default: m.ConfirmDialog })))
import { Toasts, type ToastItem } from './components/Toasts'
import { BrandMark } from './components/BrandMark'
import { getLang, setLang, t, useLang, boardNameFromFile } from './i18n'
import { DEMO_ZIP_URL } from './config'
import {
  diagnose,
  filesFromStored,
  loadGerberVendor,
  mappingOf,
  readGerberFiles,
  renderStackup,
  storedFromFiles,
  themeColors,
} from './lib/gerber'
import { parseBom } from './lib/bom'
import { filesFromDataTransfer, scanIncoming, type ImportGroup, type IncomingFile } from './lib/import'
import { defaultQuoteInputs, computeQuote } from './lib/quote'
import { downloadText } from './lib/format'
import { makeThumbnail } from './lib/thumb'
import {
  deleteProject,
  getProject,
  lastProjectId,
  listProjects,
  newId,
  rememberLast,
  saveProject,
} from './lib/db'
import type {
  BoardView,
  BomSummary,
  GerberFile,
  LayerMapping,
  ProjectMeta,
  ProjectRecord,
  ProjectStats,
  QuoteInputs,
  QuoteMode,
  RenderResult,
  RenderWarning,
} from './types'
import type { LegalKey } from './lib/legal'

type DropView = BoardView

/** Stable id so the demo card always reopens the same archive entry. */
const DEMO_ID = 'demo-board'

function errorText(error: unknown): string {
  return error instanceof Error && error.message ? error.message : t('error.unknown')
}

function nearestLayers(count: number): number {
  const allowed = [1, 2, 4, 6, 8, 10, 12]
  return allowed.find((value) => value >= count) ?? 12
}

function round1(value: number): number {
  return Math.round(value * 10) / 10
}

function statsOf(result: RenderResult): ProjectStats {
  return {
    widthMm: result.widthMm,
    heightMm: result.heightMm,
    copperLayers: result.copperLayers,
    drillHits: result.drillHits,
    minApertureMm: result.minApertureMm,
    fileCount: result.fileCount,
    panelDetected: result.panelDetected,
  }
}

function metaOnly(record: ProjectRecord): ProjectMeta {
  const { payload: _payload, ...meta } = record
  return meta
}

function warningText(warning: RenderWarning): string {
  if (warning.key === 'unmapped') return t('warn.unmapped', { n: warning.n })
  return t(`warn.${warning.key}`)
}

function svgFor(result: RenderResult, view: DropView): string {
  if (view === 'bottom') return result.bottomSvg
  if (view === 'perspective') return result.topSvgFlat
  return result.topSvg
}

function safeFileName(name: string): string {
  return (name || 'board').replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim()
}

function incomingFromFiles(files: File[]): IncomingFile[] {
  return files.map((file) => ({
    file,
    path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name,
  }))
}

export default function App() {
  const lang = useLang()
  const [projects, setProjects] = useState<ProjectMeta[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [files, setFiles] = useState<GerberFile[]>([])
  const [render, setRender] = useState<RenderResult | null>(null)
  const [view, setView] = useState<DropView>('3d')
  const [quoteMode, setQuoteMode] = useState<QuoteMode>('pcb')
  const [inputs, setInputs] = useState<QuoteInputs>(defaultQuoteInputs)
  const [bom, setBom] = useState<BomSummary | null>(null)
  const [phase, setPhase] = useState<Phase>('idle')
  const [status, setStatus] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<SaveState>('saved')
  const [thumbTick, setThumbTick] = useState(0)

  const [layersOpen, setLayersOpen] = useState(false)
  const [mappingOpen, setMappingOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [rfqOpen, setRfqOpen] = useState(false)
  const [railOpen, setRailOpen] = useState(false)
  const [dropActive, setDropActive] = useState(false)
  const [legalKey, setLegalKey] = useState<LegalKey | null>(null)
  const [renameTarget, setRenameTarget] = useState<ProjectMeta | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<ProjectMeta | null>(null)

  const [toasts, setToasts] = useState<ToastItem[]>([])
  const toastSeq = useRef(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const canvasRef = useRef<CanvasHandle>(null)
  const thumbnailRef = useRef('')
  const suppressSave = useRef(true)
  const renderSeq = useRef(0)
  const layersDirtyRef = useRef(false)
  // A freshly imported project already has its stackup; reuse it instead of
  // parsing the same Gerbers a second time when the project opens.
  const preloadedRenderRef = useRef(new Map<string, { files: GerberFile[]; render: RenderResult }>())
  const metaRef = useRef<ProjectMeta | null>(null)
  const dragDepth = useRef(0)
  const activeIdRef = useRef<string | null>(null)
  const incomingRef = useRef<(files: IncomingFile[]) => Promise<void>>(async () => undefined)

  const activeMeta = projects.find((project) => project.id === activeId) ?? null
  metaRef.current = activeMeta
  activeIdRef.current = activeId

  const pushToast = useCallback((message: string, kind: ToastItem['kind'] = 'info') => {
    const id = ++toastSeq.current
    setToasts((current) => [...current.slice(-2), { id, message, kind }])
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id))
    }, 3000)
  }, [])

  const quote = useMemo(
    () => computeQuote(inputs, quoteMode, bom),
    [inputs, quoteMode, bom, lang],
  )

  async function doRender(
    nextFiles: GerberFile[],
    options: { autoFill: boolean; reason: 'open' | 'new' | 'layers' },
  ): Promise<RenderResult | null> {
    const seq = ++renderSeq.current
    setPhase('rendering')
    setStatus(t('status.rendering'))
    setError(null)
    try {
      await loadGerberVendor()
      if (seq !== renderSeq.current) return null
      setStatus(t('status.rendering'))
      const result = await renderStackup(nextFiles, themeColors('green'), {
        knownPanel: options.reason === 'layers' && render?.panelDetected === true,
      })
      if (seq !== renderSeq.current) return null

      setRender(result)
      if (options.autoFill) {
        setInputs((current) => ({
          ...current,
          width: round1(result.widthMm),
          height: round1(result.heightMm),
          layers: nearestLayers(result.copperLayers),
        }))
      }
      setPhase('ready')
      suppressSave.current = false

      const shouldMakeThumb = !thumbnailRef.current || options.reason !== 'open'
      if (shouldMakeThumb) {
        void makeThumbnail(result.topSvg).then((thumbnail) => {
          if (seq === renderSeq.current && thumbnail) {
            thumbnailRef.current = thumbnail
            setThumbTick((value) => value + 1)
          }
        })
      }
      return result
    } catch (renderError) {
      if (seq !== renderSeq.current) return null
      const message = errorText(renderError)
      setError(message)
      setPhase('error')
      suppressSave.current = false
      pushToast(message, 'error')
      return null
    }
  }

  async function openProject(id: string): Promise<void> {
    suppressSave.current = true
    layersDirtyRef.current = false
    setActiveId(id)
    rememberLast(id)
    setRailOpen(false)
    setPhase('loading')
    setStatus(t('status.reading'))
    setError(null)
    setRender(null)
    setFiles([])
    setBom(null)
    setView('3d')
    try {
      const [record] = await Promise.all([getProject(id), loadGerberVendor()])
      if (!record) {
        setProjects((current) => current.filter((project) => project.id !== id))
        setActiveId(null)
        rememberLast(null)
        setPhase('idle')
        pushToast(t('error.unknown'), 'error')
        return
      }
      const stored = filesFromStored(record.payload.files)
      thumbnailRef.current = record.thumbnail
      setQuoteMode(record.payload.quoteMode)
      setInputs(record.payload.quoteInputs)
      setBom(record.payload.bom)
      const preloaded = preloadedRenderRef.current.get(id)
      if (preloaded) {
        preloadedRenderRef.current.delete(id)
        setFiles(preloaded.files)
        setRender(preloaded.render)
        setPhase('ready')
        suppressSave.current = false
        return
      }
      setFiles(stored)
      setStatus(t('status.rendering'))
      const result = await doRender(stored, { autoFill: false, reason: 'open' })
      if (!result) return
    } catch (loadError) {
      const message = errorText(loadError)
      setError(message)
      setPhase('error')
      pushToast(message, 'error')
    }
  }

  useEffect(() => {
    let alive = true
    void (async () => {
      try {
        const list = await listProjects()
        if (!alive) return
        setProjects(list)
        const remembered = lastProjectId()
        const target = list.find((project) => project.id === remembered) ?? list[0]
        if (target) void openProject(target.id)
      } catch {
        // A blocked IndexedDB still allows a one-off upload for this session.
      }
    })()
    return () => {
      alive = false
    }
    // Runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!activeId || !render || phase !== 'ready' || suppressSave.current) return
    setSaved('saving')
    const timer = window.setTimeout(async () => {
      const meta = metaRef.current
      if (!meta) return
      const now = Date.now()
      const record: ProjectRecord = {
        ...meta,
        updatedAt: now,
        thumbnail: thumbnailRef.current || meta.thumbnail,
        stats: statsOf(render),
        mode: quoteMode,
        total: quote.total,
        payload: {
          files: storedFromFiles(files),
          quoteMode,
          quoteInputs: inputs,
          bom,
        },
      }
      try {
        await saveProject(record)
        setProjects((current) => current.map((project) => (project.id === record.id ? metaOnly(record) : project)))
        setSaved('saved')
      } catch {
        setSaved('failed')
        pushToast(t('toast.storageFull'), 'error')
      }
    }, 700)
    return () => window.clearTimeout(timer)
  }, [activeId, render, phase, files, quoteMode, inputs, bom, thumbTick, quote.total, pushToast])

  function openFilePicker(): void {
    fileInputRef.current?.click()
  }

  function openFolderPicker(): void {
    folderInputRef.current?.click()
  }

  async function attachBomText(name: string, text: string): Promise<void> {
    try {
      const summary = parseBom(text, name)
      setBom(summary)
      setInputs((current) => ({
        ...current,
        uniqueParts: summary.uniqueParts,
        placements: summary.placements,
      }))
      pushToast(t('toast.bomLoaded', {
        name: summary.fileName,
        parts: summary.uniqueParts,
        points: summary.placements,
      }), 'ok')
    } catch (bomError) {
      pushToast(errorText(bomError), 'error')
    }
  }

  async function attachBomFile(file: File): Promise<void> {
    if (!activeIdRef.current) {
      pushToast(t('toast.bomOnly'), 'error')
      return
    }
    const text = await file.text()
    await attachBomText(file.name, text)
  }

  async function importGroup(group: ImportGroup, options?: { id?: string; name?: string }): Promise<void> {
    await loadGerberVendor()
    setStatus(t('status.reading'))
    const result = await readGerberFiles(group.files)
    setStatus(t('status.detecting'))

    const nextBom = group.bom
    const id = options?.id ?? newId()
    const now = Date.now()
    const name = options?.name ?? group.name ?? boardNameFromFile(result.files[0]?.name ?? '')
    const meta: ProjectMeta = {
      id,
      name,
      createdAt: now,
      updatedAt: now,
      stats: {
        widthMm: 0,
        heightMm: 0,
        copperLayers: 0,
        drillHits: 0,
        minApertureMm: null,
        fileCount: result.files.length,
        panelDetected: false,
      },
      thumbnail: '',
      mode: 'pcb',
      total: 0,
    }

    suppressSave.current = true
    thumbnailRef.current = ''
    setProjects((current) => [meta, ...current])
    setActiveId(id)
    activeIdRef.current = id
    rememberLast(id)
    setFiles(result.files)
    setRender(null)
    setView('3d')
    setQuoteMode('pcb')
    setInputs({
      ...defaultQuoteInputs(),
      ...(nextBom ? { uniqueParts: nextBom.uniqueParts, placements: nextBom.placements } : {}),
    })
    setBom(nextBom)
    setSaved('saved')

    const rendered = await doRender(result.files, { autoFill: true, reason: 'new' })
    if (!rendered) {
      setProjects((current) => current.filter((project) => project.id !== id))
      setActiveId(null)
      rememberLast(null)
      setPhase('error')
      return
    }
    if (nextBom) {
      pushToast(t('toast.bomLoaded', {
        name: nextBom.fileName,
        parts: nextBom.uniqueParts,
        points: nextBom.placements,
      }), 'ok')
    }
  }

  async function importGroups(groups: ImportGroup[]): Promise<void> {
    await loadGerberVendor()
    const created: ProjectMeta[] = []
    const usedNames = new Set(projects.map((project) => project.name))
    let skipped = 0

    for (let index = 0; index < groups.length; index += 1) {
      const group = groups[index]
      setStatus(t('status.importing', { current: index + 1, total: groups.length, name: group.name }))
      try {
        const result = await readGerberFiles(group.files)
        const rendered = await renderStackup(result.files, themeColors('green'))
        let thumbnail = ''
        try {
          thumbnail = await makeThumbnail(rendered.topSvg)
        } catch {
          thumbnail = ''
        }

        let name = group.name || boardNameFromFile(result.files[0]?.name ?? '')
        if (usedNames.has(name)) {
          let suffix = 2
          while (usedNames.has(`${name} (${suffix})`)) suffix += 1
          name = `${name} (${suffix})`
        }
        usedNames.add(name)

        const now = Date.now()
        const id = newId()
        const bom = group.bom
        const quoteInputs: QuoteInputs = {
          ...defaultQuoteInputs(),
          width: round1(rendered.widthMm),
          height: round1(rendered.heightMm),
          layers: nearestLayers(rendered.copperLayers),
          ...(bom ? { uniqueParts: bom.uniqueParts, placements: bom.placements } : {}),
        }
        const record: ProjectRecord = {
          id,
          name,
          createdAt: now,
          updatedAt: now,
          stats: statsOf(rendered),
          thumbnail,
          mode: 'pcb',
          total: 0,
          payload: {
            files: storedFromFiles(result.files),
            quoteMode: 'pcb',
            quoteInputs,
            bom,
          },
        }
        await saveProject(record)
        preloadedRenderRef.current.set(id, { files: result.files, render: rendered })
        created.push(metaOnly(record))
      } catch {
        skipped += 1
      }
      // Yield so the progress line paints between large panels.
      await new Promise((resolve) => window.setTimeout(resolve, 0))
    }

    if (created.length === 0) throw new Error(t('error.noGerber'))
    setProjects((current) => [...created, ...current])
    pushToast(
      skipped > 0
        ? t('toast.importSkipped', { n: created.length, skipped })
        : t('toast.imported', { n: created.length }),
      skipped > 0 ? 'info' : 'ok',
    )
    await openProject(created[0].id)
  }

  async function handleIncoming(incoming: IncomingFile[], options?: { id?: string; name?: string }): Promise<void> {
    const csvFiles = incoming.filter((item) => item.file.name.toLowerCase().endsWith('.csv'))
    const boardFiles = incoming.filter((item) => !item.file.name.toLowerCase().endsWith('.csv'))

    if (boardFiles.length === 0) {
      if (csvFiles[0]) await attachBomFile(csvFiles[0].file)
      return
    }

    setPhase('loading')
    setStatus(t('status.scanning'))
    setError(null)
    setLayersOpen(false)

    try {
      const scan = await scanIncoming(incoming)
      if (scan.groups.length === 1) {
        await importGroup(scan.groups[0], options)
      } else {
        await importGroups(scan.groups)
      }
      if (scan.truncated) {
        pushToast(t('toast.importLimited', { n: scan.groups.length }), 'info')
      }
    } catch (uploadError) {
      const message = errorText(uploadError)
      pushToast(message, 'error')
      if (activeIdRef.current && render) {
        setError(null)
        setPhase('ready')
      } else {
        setError(message)
        setPhase('error')
      }
    }
  }

  incomingRef.current = handleIncoming

  useEffect(() => {
    // React has no typed prop for these Chromium/WebKit directory attributes.
    folderInputRef.current?.setAttribute('webkitdirectory', '')
    folderInputRef.current?.setAttribute('directory', '')
  }, [])

  async function loadDemo(): Promise<void> {
    if (projects.some((project) => project.id === DEMO_ID)) {
      await openProject(DEMO_ID)
      return
    }
    try {
      const response = await fetch(DEMO_ZIP_URL)
      if (!response.ok) throw new Error(t('toast.demoFailed'))
      const blob = await response.blob()
      const file = new File([blob], 'Demo board.zip', { type: 'application/zip' })
      await handleIncoming([{ file, path: 'Demo board.zip' }], { id: DEMO_ID, name: 'Demo board' })
    } catch {
      pushToast(t('toast.demoFailed'), 'error')
    }
  }

  async function downloadProject(id: string): Promise<void> {
    const meta = projects.find((project) => project.id === id)
    if (id === activeId && render) {
      downloadText(`${safeFileName(meta?.name ?? 'board')}.svg`, svgFor(render, view), 'image/svg+xml')
      pushToast(t('toast.svgDownloaded'), 'ok')
      return
    }
    try {
      const record = await getProject(id)
      if (!record) return
      await loadGerberVendor()
      const result = await renderStackup(filesFromStored(record.payload.files), themeColors('green'))
      downloadText(`${safeFileName(record.name)}.svg`, result.topSvg, 'image/svg+xml')
      pushToast(t('toast.svgDownloaded'), 'ok')
    } catch (downloadError) {
      pushToast(errorText(downloadError), 'error')
    }
  }

  async function renameProject(id: string, name: string): Promise<void> {
    const meta = projects.find((project) => project.id === id)
    if (!meta) return
    setProjects((current) => current.map((project) => (project.id === id ? { ...project, name, updatedAt: Date.now() } : project)))
    if (id === activeId && render) {
      const record: ProjectRecord = {
        ...meta,
        name,
        updatedAt: Date.now(),
        thumbnail: thumbnailRef.current || meta.thumbnail,
        stats: statsOf(render),
        mode: quoteMode,
        total: quote.total,
        payload: { files: storedFromFiles(files), quoteMode, quoteInputs: inputs, bom },
      }
      try {
        await saveProject(record)
      } catch {
        pushToast(t('toast.storageFull'), 'error')
      }
    } else {
      try {
        const record = await getProject(id)
        if (record) {
          record.name = name
          record.updatedAt = Date.now()
          await saveProject(record)
        }
      } catch {
        // The meta list still shows the new name for this session.
      }
    }
    pushToast(t('toast.renamed'), 'ok')
  }

  async function removeProject(id: string): Promise<void> {
    try {
      await deleteProject(id)
    } catch {
      // Continue with the in-memory removal; the record can be cleaned later.
    }
    const remaining = projects.filter((project) => project.id !== id)
    setProjects(remaining)
    if (id === activeId) {
      setActiveId(null)
      rememberLast(null)
      setFiles([])
      setRender(null)
      setBom(null)
      setPhase('idle')
      setError(null)
      thumbnailRef.current = ''
      const next = remaining[0]
      if (next) void openProject(next.id)
    }
    pushToast(t('toast.projectDeleted'), 'ok')
  }

  function changeMode(next: QuoteMode): void {
    setQuoteMode(next)
    setInputs((current) => ({
      ...current,
      lead: next === 'pcba' && current.lead === '24h' ? 'rush' : current.lead,
    }))
  }

  function patchInputs(patch: Partial<QuoteInputs>): void {
    setInputs((current) => ({ ...current, ...patch }))
  }

  function refreshDiagnostics(next: GerberFile[]): void {
    setRender((current) =>
      current
        ? { ...current, ...diagnose(next, current.copperLayers, current.panelDetected) }
        : current,
    )
  }

  function toggleLayer(id: string, enabled: boolean): void {
    const next = files.map((file) => (file.id === id ? { ...file, enabled } : file))
    setFiles(next)
    // In 3D the layers are separate meshes; re-rendering the whole 2D stackup
    // here is what made a switch feel like a page reload on a dense panel.
    if (view === '3d') {
      layersDirtyRef.current = true
      // Keep the quote panel's presence checks in step with the visible
      // layers; only the 2D SVG is deferred.
      refreshDiagnostics(next)
      return
    }
    void doRender(next, { autoFill: false, reason: 'layers' })
  }

  function setAllLayers(enabled: boolean): void {
    const next = files.map((file) => ({ ...file, enabled }))
    setFiles(next)
    if (view === '3d') {
      layersDirtyRef.current = true
      refreshDiagnostics(next)
      return
    }
    void doRender(next, { autoFill: false, reason: 'layers' })
  }

  function changeView(next: DropView): void {
    if (next === view) return
    const leaving3d = view === '3d' && next !== '3d'
    setView(next)
    if (leaving3d && layersDirtyRef.current) {
      layersDirtyRef.current = false
      void doRender(files, { autoFill: false, reason: 'layers' })
    }
  }

  function changeMapping(id: string, mapping: LayerMapping): void {
    const next: GerberFile[] = files.map((file) => {
      if (file.id !== id) return file
      const detected = mappingOf(file)
      const same = detected.side === mapping.side && detected.type === mapping.type
      return { ...file, override: same ? null : mapping }
    })
    setFiles(next)
    void doRender(next, { autoFill: false, reason: 'layers' })
  }

  function resetMappings(): void {
    const next = files.map((file) => ({ ...file, override: null }))
    setFiles(next)
    void doRender(next, { autoFill: false, reason: 'layers' })
  }

  function downloadActive(): void {
    if (activeId) void downloadProject(activeId)
  }

  useEffect(() => {
    const onDragEnter = (event: DragEvent): void => {
      if (!event.dataTransfer?.types.includes('Files')) return
      event.preventDefault()
      dragDepth.current += 1
      setDropActive(true)
    }
    const onDragOver = (event: DragEvent): void => {
      if (!event.dataTransfer?.types.includes('Files')) return
      event.preventDefault()
    }
    const onDragLeave = (): void => {
      dragDepth.current = Math.max(0, dragDepth.current - 1)
      if (dragDepth.current === 0) setDropActive(false)
    }
    const onDrop = (event: DragEvent): void => {
      if (!event.dataTransfer?.files.length) return
      event.preventDefault()
      dragDepth.current = 0
      setDropActive(false)
      void filesFromDataTransfer(event.dataTransfer)
        .then((incoming) => incomingRef.current(incoming))
        .catch(() => undefined)
    }
    window.addEventListener('dragenter', onDragEnter)
    window.addEventListener('dragover', onDragOver)
    window.addEventListener('dragleave', onDragLeave)
    window.addEventListener('drop', onDrop)
    return () => {
      window.removeEventListener('dragenter', onDragEnter)
      window.removeEventListener('dragover', onDragOver)
      window.removeEventListener('dragleave', onDragLeave)
      window.removeEventListener('drop', onDrop)
    }
  }, [])

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null
      const typing = target?.closest('input, textarea, select, [contenteditable="true"]')
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setPaletteOpen((value) => !value)
        return
      }
      if (typing) return
      if (event.key === 'Escape') {
        setLayersOpen(false)
        return
      }
      if (document.querySelector('.dialog-overlay')) return
      if (!activeId) {
        if (event.key.toLowerCase() === 'u') openFilePicker()
        return
      }
      if (event.key === '1') changeView('top')
      else if (event.key === '2') changeView('bottom')
      else if (event.key === '3') changeView('perspective')
      else if (event.key === '4') changeView('3d')
      else if (event.key.toLowerCase() === 'f') canvasRef.current?.fit()
      else if (event.key.toLowerCase() === 'l') setLayersOpen((value) => !value)
      else if (event.key.toLowerCase() === 'u') openFilePicker()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeId])

  return (
    <div className={`app ${railOpen ? 'rail-open' : ''}`}>
      <ProjectRail
        projects={projects.filter((project) => project.id !== DEMO_ID)}
        activeId={activeId}
        demoActive={activeId === DEMO_ID}
        lang={lang}
        onLang={(next) => setLang(next)}
        onOpen={(id) => { setRailOpen(false); void openProject(id) }}
        onNew={openFilePicker}
        onNewFolder={openFolderPicker}
        onDemo={() => { setRailOpen(false); void loadDemo() }}
        onRename={(meta) => setRenameTarget(meta)}
        onDelete={(meta) => setDeleteTarget(meta)}
        onDownload={(meta) => void downloadProject(meta.id)}
        onLegal={(key) => setLegalKey(key)}
        onCloseMobile={() => setRailOpen(false)}
      />

      {railOpen ? <button className="rail-scrim" type="button" aria-label={t('common.close')} onClick={() => setRailOpen(false)} /> : null}

      {!activeId ? (
        <header className="mobile-bar">
          <div className="brand">
            <span className="brand-mark"><BrandMark size={19} /></span>
            <span className="brand-type"><strong>PCBA</strong><span>GLOBAL VIEW</span></span>
          </div>
          <button className="icon-btn" type="button" onClick={() => setRailOpen(true)} aria-label={t('rail.expand')}>
            <Menu size={17} />
          </button>
        </header>
      ) : null}

      <main className="workspace">
        {activeId ? (
          <CanvasStage
            ref={canvasRef}
            render={render}
            files={files}
            view={view}
            onView={changeView}
            name={activeMeta?.name ?? t('project.untitled')}
            phase={phase}
            status={status}
            error={error}
            warnings={(render?.warnings ?? []).map(warningText)}
            saved={saved}
            layerCount={files.length}
            enabledCount={files.filter((file) => file.enabled).length}
            onLayers={() => setLayersOpen((value) => !value)}
            onToggleLayer={toggleLayer}
            onDownload={downloadActive}
            onRename={() => { if (activeMeta) setRenameTarget(activeMeta) }}
            onRetry={() => {
              if (activeId) void openProject(activeId)
            }}
            onDemo={() => void loadDemo()}
            onMobileRail={() => setRailOpen(true)}
          />
        ) : (
          <EmptyState
            onChoose={openFilePicker}
            onChooseFolder={openFolderPicker}
            onDemo={() => void loadDemo()}
            projects={projects}
            onOpen={(id) => void openProject(id)}
          />
        )}

        {activeId ? (
          <Inspector
            meta={activeMeta}
            render={render}
            mode={quoteMode}
            onMode={changeMode}
            inputs={inputs}
            onInputs={patchInputs}
            bom={bom}
            onBomFile={(file) => void attachBomFile(file)}
            onBomClear={() => setBom(null)}
            quote={quote}
            onRename={() => { if (activeMeta) setRenameTarget(activeMeta) }}
            onDelete={() => { if (activeMeta) setDeleteTarget(activeMeta) }}
            onDownload={downloadActive}
            onSubmit={() => setRfqOpen(true)}
          />
        ) : null}

        <LayerPanel
          open={layersOpen}
          files={files}
          onClose={() => setLayersOpen(false)}
          onToggle={toggleLayer}
          onAll={setAllLayers}
          onManage={() => { setLayersOpen(false); setMappingOpen(true) }}
        />
      </main>

      <Suspense fallback={null}>
        {mappingOpen ? (
          <FileMappingDialog
            open
            files={files}
            onClose={() => setMappingOpen(false)}
            onMapping={changeMapping}
            onReset={resetMappings}
          />
        ) : null}

        {rfqOpen ? (
          <RfqDialog
            open
            onClose={() => setRfqOpen(false)}
            project={activeMeta}
            quote={quote}
            files={files}
            bom={bom}
            inputs={inputs}
          />
        ) : null}

        {paletteOpen ? (
          <CommandPalette
            open
            onOpenChange={setPaletteOpen}
            projects={projects}
            activeId={activeId}
            lang={getLang()}
            onOpenProject={(id) => void openProject(id)}
            onUpload={openFilePicker}
            onUploadFolder={openFolderPicker}
            onDemo={() => void loadDemo()}
            onFit={() => canvasRef.current?.fit()}
            onDownload={downloadActive}
            onToggleLang={() => setLang(lang === 'zh' ? 'en' : 'zh')}
            onDelete={() => { if (activeMeta) setDeleteTarget(activeMeta) }}
          />
        ) : null}

        {legalKey ? (
          <LegalDialog open initial={legalKey} lang={lang} onClose={() => setLegalKey(null)} />
        ) : null}

        {renameTarget ? (
          <RenameDialog
            open
            initial={renameTarget.name}
            onClose={() => setRenameTarget(null)}
            onSave={(name) => void renameProject(renameTarget.id, name)}
          />
        ) : null}

        {deleteTarget ? (
          <ConfirmDialog
            open
            title={t('project.deleteTitle')}
            body={t('project.deleteBody')}
            confirmLabel={t('common.delete')}
            onClose={() => setDeleteTarget(null)}
            onConfirm={() => void removeProject(deleteTarget.id)}
          />
        ) : null}
      </Suspense>

      <Toasts items={toasts} onDismiss={(id) => setToasts((current) => current.filter((toast) => toast.id !== id))} />

      <AnimatePresence>
        {dropActive ? (
          <motion.div
            className="drop-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.14 }}
          >
            <div className="drop-overlay-card">
              <ArrowDownToLine size={26} />
              <strong>{t('drop.title')}</strong>
              <span>{t('drop.body')}</span>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        accept=".zip,.gbr,.ger,.gtl,.gbl,.gto,.gbo,.gts,.gbs,.gtp,.gbp,.gm1,.gko,.gml,.drl,.nc,.xln,.drd,.art,.pho,.cmp,.sol,.stc,.sts,.plc,.pls,.txt,.csv"
        onChange={(event) => {
          const incoming = incomingFromFiles(Array.from(event.target.files ?? []))
          event.target.value = ''
          if (incoming.length > 0) void handleIncoming(incoming)
        }}
      />

      <input
        ref={folderInputRef}
        type="file"
        hidden
        multiple
        onChange={(event) => {
          const incoming = incomingFromFiles(Array.from(event.target.files ?? []))
          event.target.value = ''
          if (incoming.length > 0) void handleIncoming(incoming)
        }}
      />

    </div>
  )
}
